package components.colourToneItem

import common.ECssClasses
import components.Spacer.Spacer
import components.toast.ToastItem
import core.AppContext
import core.enums.EImages
import core.enums.ELocalizationKeys
import core.extensions.toClassName
import core.localStorage.LocalStorageDAO
import core.localization.Localization
import core.piwik.Piwik
import core.util.ColourHelper
import csstype.Auto
import csstype.BackgroundColor
import csstype.Display
import csstype.FlexDirection
import csstype.FlexWrap
import csstype.NamedColor
import csstype.integer
import csstype.number
import csstype.px
import emotion.react.css
import module.watchlist.ColourListObject
import react.FC
import react.PropsWithClassName
import react.dom.html.ReactHTML
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import react.router.Navigate
import react.useContext
import react.useState
import screens.EScreens
import setUnsafeInnerHTML

/**
 * Copyright (c) emgress GmbH. All rights reserved.
 *
 * Created on : 27.10.22
 * Author : mariorosshirt
 */

external interface ColourToneItemProps : PropsWithClassName {
    var mColourObject: ColourListObject
    var mDeleteFunction: () -> Unit
    var mColourToneItemLeftButtonType: EColourToneItemLeftButtonType?
    var mColourToneItemLeftButtonToastMessage: (toastItem: ToastItem?) -> Unit
    var mNavigate: String?
}

val ColourToneItem = FC<ColourToneItemProps> { props ->

    val mContext by useContext(AppContext)
    var (mLocalStorage, mLocalStorageDispatch) = mContext.mLocalStorageReducer
    val mColourObject = props.mColourObject
    val mOnWatchlist = mLocalStorage.mWatchlist.any { it.mUnique == mColourObject.mUnique }
    val mOnProductList = mLocalStorage.mProductList.any { it.mUnique == mColourObject.mUnique }

    val mColourToneItemLeftButtonType = props.mColourToneItemLeftButtonType ?: EColourToneItemLeftButtonType.WATCH_LIST

    val mIsColourBlack = ColourHelper.getContrastIsBlack(mColourObject.mColourCode)
    val mContrastColour = if (mIsColourBlack) NamedColor.black else NamedColor.white

    var navigate: String? by useState(props.mNavigate)

    fun onCardClick() {
        mLocalStorageDispatch.invoke(
            LocalStorageDAO.ELocalStorageAction.ADD_DETAIL_COLOUR(mColourObject)
        )
        navigate = EScreens.COLOUR_TONE_DETAIL.mPath
    }

    div {
        css(ECssClasses.COLOUR_CARD_ITEM.toClassName(), props.className) {
            flexGrow = number(1.0)
            display = Display.flex
            flexDirection = FlexDirection.column
            backgroundColor = mColourObject.mColourCode.unsafeCast<BackgroundColor>()
            //height = 100.px
            borderRadius = 4.px
        }
        onClick = {
            onCardClick()
        }
        div {
            css() {
                display = Display.flex
                flexGrow = number(1.0)
                flexWrap = FlexWrap.wrapReverse
            }
            ReactHTML.span {
                css(ECssClasses.PRODUCT_CARD_ITEM_SMALL_FONT.toClassName()) {
                    color = mContrastColour
                    zIndex = integer(2)
                    marginBottom = Auto.auto
                    paddingLeft = 26.px
                    paddingTop = 16.px
                }
                +mColourObject.mColourCollection
            }
            div {
                css {
                    display = Display.flex
                    flexGrow = number(1.0)
                }
                Spacer()
                div {
                    css(ECssClasses.CURSOR_HOVER.toClassName()) {
                        marginTop = 16.px
                        marginLeft = 16.px
                        marginRight = 36.px
                    }
                    img {
                        css {
                            height = 24.px
                            width = 24.px
                        }
                        src = when (mColourToneItemLeftButtonType) {
                            EColourToneItemLeftButtonType.WATCH_LIST -> EImages.WATCHLIST_ICON(
                                EImages.EColor.fromColorHex(
                                    mColourObject.mColourCode
                                ), EImages.EType.fromIsOnList(mOnWatchlist)
                            ).mRelativePath

                            EColourToneItemLeftButtonType.PRODUCT_LIST -> EImages.SHOPPING_CART_ICON(
                                EImages.EColor.fromColorHex(
                                    mColourObject.mColourCode
                                ), EImages.EType.fromIsOnList(mOnProductList)
                            ).mRelativePath
                        }
                    }
                    onClick = {
                        when (mColourToneItemLeftButtonType) {
                            EColourToneItemLeftButtonType.WATCH_LIST -> {
                                mLocalStorageDispatch(
                                    LocalStorageDAO.ELocalStorageAction.ADD_OR_DELETE_FROM_WATCHLIST(
                                        mColourObject
                                    )
                                ).also {
                                    props.mColourToneItemLeftButtonToastMessage(
                                        if (mLocalStorage.mWatchlist.contains(mColourObject)) {
                                            ToastItem(Localization.getLocalizationKey(ELocalizationKeys.CORE_TOAST_DELETE_FROM_WATCH_LIST.name))
                                        } else {
                                            ToastItem(Localization.getLocalizationKey(ELocalizationKeys.CORE_TOAST_ADD_TO_WATCH_LIST.name))
                                        }
                                    )
                                    if (mLocalStorage.mWatchlist.contains(mColourObject).not()) {
                                        Piwik.trackContentInteraction(
                                            Piwik.CATEGORY_WISH_LIST,
                                            Piwik.NAME_WISHLIST_COLOR_ADD,
                                            mColourObject.mUnique,
                                            Piwik.TARGET_COLOR
                                        )
                                    }
                                }
                            }

                            EColourToneItemLeftButtonType.PRODUCT_LIST -> {
                                mLocalStorageDispatch(
                                    LocalStorageDAO.ELocalStorageAction.ADD_OR_DELETE_FROM_PRODUCTLIST(
                                        mColourObject
                                    )
                                ).also {
                                    props.mColourToneItemLeftButtonToastMessage(
                                        if (mLocalStorage.mProductList.contains(mColourObject)) {
                                            ToastItem(Localization.getLocalizationKey(ELocalizationKeys.CORE_TOAST_DELETE_FROM_PRODUCT_LIST.name))
                                        } else {
                                            ToastItem(Localization.getLocalizationKey(ELocalizationKeys.CORE_TOAST_ADD_TO_PRODUCT_LIST.name))
                                        }
                                    )
                                    if (mLocalStorage.mProductList.contains(mColourObject).not()) {
                                        Piwik.trackContentInteraction(
                                            Piwik.CATEGORY_PRODUCT_LIST,
                                            Piwik.NAME_PRODUCTLIST_COLOR_ADD,
                                            mColourObject.mUnique,
                                            Piwik.TARGET_COLOR
                                        )
                                    }
                                }
                            }
                        }
                        it.stopPropagation()
                    }
                }
                div {
                    css(ECssClasses.CURSOR_HOVER.toClassName()) {
                        marginTop = 16.px
                        marginRight = 16.px
                    }
                    img {
                        css {
                            height = 24.px
                        }
                        src =
                            if (mIsColourBlack) EImages.DELETE_ICON_BLACK.mRelativePath else EImages.DELETE_ICON_WHITE.mRelativePath
                    }
                    onClick = {
                        props.mDeleteFunction()
                        it.stopPropagation()
                    }
                }
            }
        }
        div {
            Spacer()
            div {
                css {
                    paddingBottom = 15.px
                    paddingRight = 26.px
                    paddingLeft = 26.px
                }
                ReactHTML.span {
                    css(ECssClasses.PRODUCT_CARD_ITEM_MEDIUM_FONT_WEIGHTED.toClassName()) {
                        color = mContrastColour
                    }
                    setUnsafeInnerHTML("<b>${mColourObject.mArtNr}</b></br>")
                }
                ReactHTML.span {
                    css(ECssClasses.PRODUCT_CARD_ITEM_MEDIUM_FONT.toClassName()) {
                        color = mContrastColour
                    }
                    if(mColourObject.mName != ""){
                        setUnsafeInnerHTML(mColourObject.mName)
                    }else{
                        setUnsafeInnerHTML("</br>")
                    }
                }
            }
        }

        navigate?.let {
            Navigate {
                to = it
            }
        }
    }
}